import { useNavigate } from 'react-router';
import {
    RequestStatus,
    ServerRequestState,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { useForm } from 'react-hook-form';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import {
    mandatoryFieldErrorMessage,
    maxFieldErrorMessage,
    minFieldErrorMessage,
    tooManyCharsMessage,
} from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { ParkingPortalLayoutWithHeader } from '../mobile/layout/ParkingPortalLayoutWithHeader.tsx';
import { Box, Button } from '@mui/material';
import Alert from '@mui/material/Alert';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { SubmitButton } from '../ui/buttons/SubmitButton.tsx';
import { useOffstreetEventCardsOutlet } from './OperatorOffstreetEventCards.tsx';

const enum OffstreetEventAddStackFields {
    stackName = 'stackName',
    numberEventContracts = 'numberEventContracts',
}

interface OffstreetEventAddStackFieldValues {
    [OffstreetEventAddStackFields.stackName]: string;
    [OffstreetEventAddStackFields.numberEventContracts]: number | null;
}

interface OffstreetEventAddStackPayload {
    stackName: string;
    numberEventContracts: number | null;
    eventId: string;
}

export interface OffstreetEventStackErrorResonse {
    error: OffstreetEventStackError;
}

const enum OffstreetEventStackError {
    STACK_NAME_ALREADY_EXISTS = 'STACK_NAME_ALREADY_EXISTS',
}

export const MAX_EVENT_STACK_CONTRACTS = 1000;

export function OperatorOffstreetEventsAddStack() {
    const {
        event: { eventId },
        refetchEvent,
    } = useOffstreetEventCardsOutlet();
    const navigate = useNavigate();
    const [createOffstreetEventStackState, createOffstreetEventStack] =
        useServerWrite<
            OffstreetEventAddStackPayload,
            null,
            OffstreetEventStackErrorResonse
        >(payload => ({
            url: `/ui-api/operator-account/offstreet-events/${payload.eventId}/stack/add`,
        }));
    useServerSuccessEffect(createOffstreetEventStackState, () => {
        refetchEvent();
        navigate('..');
    });

    const { control, handleSubmit } =
        useForm<OffstreetEventAddStackFieldValues>({
            defaultValues: {
                [OffstreetEventAddStackFields.stackName]: '',
                [OffstreetEventAddStackFields.numberEventContracts]: null,
            },
            mode: 'onBlur',
        });

    const maxLengthDescription = 30;
    const descriptionTooLong = useTranslation(
        tooManyCharsMessage(maxLengthDescription),
    );
    const minNumberOfEventContracts = 1;
    const minimumMessage = useTranslation(
        minFieldErrorMessage(minNumberOfEventContracts),
    );
    const maximumMessage = useTranslation(
        maxFieldErrorMessage(MAX_EVENT_STACK_CONTRACTS),
    );
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    const savePending =
        createOffstreetEventStackState.status == RequestStatus.PENDING;

    return (
        <OperatorRoutedModal
            variant={ModalVariant.TINY}
            backUrl={'..'}
            render={() => (
                <ParkingPortalLayoutWithHeader
                    title={
                        <Localized
                            de="Stapel generieren"
                            fr="Générer set"
                            it="Genera set"
                            en="Generate stack"
                        />
                    }
                    backTo={'..'}
                >
                    <Box
                        component="form"
                        onSubmit={handleSubmit(
                            (data: OffstreetEventAddStackFieldValues) => {
                                createOffstreetEventStack({ ...data, eventId });
                            },
                        )}
                        sx={{
                            padding: '25px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        <OperatorOffstreetEventsAddStackErrorModal
                            OffstreetEventStackState={
                                createOffstreetEventStackState
                            }
                        />

                        <ReactHookFormTextField
                            label={
                                <Localized
                                    de="Bezeichnung"
                                    fr="Désignation"
                                    it="Descrizione"
                                    en="Description"
                                />
                            }
                            name={OffstreetEventAddStackFields.stackName}
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: mandatoryFieldMessage,
                                },
                                maxLength: {
                                    value: maxLengthDescription,
                                    message: descriptionTooLong,
                                },
                                minLength: {
                                    value: 1,
                                    message: mandatoryFieldMessage,
                                },
                            }}
                        />
                        <ReactHookFormTextField
                            label={
                                <Localized
                                    de="Anzahl"
                                    fr="Quantité"
                                    it="Quantità"
                                    en="Quantity"
                                />
                            }
                            type="number"
                            name={
                                OffstreetEventAddStackFields.numberEventContracts
                            }
                            control={control}
                            rules={{
                                min: {
                                    value: minNumberOfEventContracts,
                                    message: minimumMessage,
                                },
                                max: {
                                    value: MAX_EVENT_STACK_CONTRACTS,
                                    message: maximumMessage,
                                },
                                required: {
                                    value: true,
                                    message: mandatoryFieldMessage,
                                },
                            }}
                            sx={{
                                paddingBottom: '25px',
                                width: '150px',
                            }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                paddingTop: '8px',
                            }}
                        >
                            <Button
                                onClick={() => navigate('..')}
                                variant="outlined"
                                disabled={savePending}
                            >
                                <Localized
                                    de="Abbrechen"
                                    fr="Annuler"
                                    it="Annulla"
                                    en="Cancel"
                                />
                            </Button>
                            <SubmitButton
                                sx={{ marginLeft: '5px' }}
                                loading={savePending}
                                label={
                                    <Localized
                                        de="Speichern"
                                        fr="Sauvegarder"
                                        it="Salva"
                                        en="Save"
                                    />
                                }
                            />
                        </Box>
                    </Box>
                </ParkingPortalLayoutWithHeader>
            )}
        />
    );
}

export function OperatorOffstreetEventsAddStackErrorModal({
    OffstreetEventStackState,
}: {
    OffstreetEventStackState: ServerRequestState<
        null,
        OffstreetEventStackErrorResonse
    >;
}) {
    if (OffstreetEventStackState.status != RequestStatus.ERROR) {
        return null;
    }
    switch (OffstreetEventStackState.data?.error ?? null) {
        case OffstreetEventStackError.STACK_NAME_ALREADY_EXISTS:
            return (
                <Alert severity="error" sx={{ margin: '0' }}>
                    <Localized
                        de="Diese Bezeichnung existiert bereits für diesen Stapel, wählen Sie eine andere."
                        fr="Cette désignation existe déjà pour ce set, choisissez-en une autre."
                        it="Questa descrizione esiste già per questo set, scegline un'altra."
                        en="This description already exists for this stack, choose another one."
                    />
                </Alert>
            );
        case null:
            return (
                <Alert severity="error" sx={{ margin: '0' }}>
                    <Localized
                        de="Es ist ein Fehler aufgetreten."
                        fr="Une erreur est survenue."
                        it="Si è verificato un errore."
                        en="An error has occurred."
                    />
                </Alert>
            );
    }
}
